/**
 * Monaco completion helpers for Control Designer ScopeScript editor.
 */

import {
    isValidJsIdentifier,
    listScopeScriptMembers,
} from './controlDesignerScopeScript.js';
import { CONTROL_SCOPE_SCRIPT_LANGUAGE_ID } from './controlDesignerScopeScriptLanguage.js';
import { buildControlModelDotCompletions } from './controlDesignerControlModelMembers.js';
import { buildApiDotCompletions } from './controlDesignerScopeScriptRuntimeMembers.js';

const INTERNAL_COMMAND_VISIBILITY = new Set(['InternalOnly', 'InternalAndExternal']);

const IDENTIFIER_CHAR = /[A-Za-z0-9_$]/;

/**
 * @param {string} textBeforeCursor
 * @returns {string|null}
 */
export function extractMemberAccessExpression(textBeforeCursor) {
    if (!textBeforeCursor || !textBeforeCursor.endsWith('.'))
        return null;

    let i = textBeforeCursor.length - 2;
    let expression = '';

    while (i >= 0) {
        const ch = textBeforeCursor[i];

        if (ch === ' ' || ch === '\t' || ch === '\n') {
            if (expression)
                break;
            i--;
            continue;
        }

        if (';{}[](),'.includes(ch) && !expression)
            break;

        if (!IDENTIFIER_CHAR.test(ch) && ch !== '.')
            break;

        expression = ch + expression;
        i--;
    }

    return expression || null;
}

/**
 * @param {string} name
 * @returns {boolean}
 */
export function isUsableControlName(name) {
    const trimmed = String(name || '').trim();
    return trimmed !== '' && trimmed !== 'Unnamed' && isValidJsIdentifier(trimmed);
}

/**
 * @param {unknown} node
 * @returns {boolean}
 */
export function isEmbeddedUserControlNode(node) {
    return !!node
        && typeof node === 'object'
        && node.PrimitiveControl === false
        && String(node.ControlURL || '').trim() !== '';
}

/**
 * @param {unknown} node
 * @returns {string}
 */
export function resolveControlScopeName(node) {
    if (isEmbeddedUserControlNode(node))
        return String(node.Name || '').trim();

    return String(node.ControlData?.Name || '').trim();
}

/**
 * @param {string} expression
 * @returns {{ root: 'Control' | 'this', member: string | null } | null}
 */
export function parseScopeAccessExpression(expression) {
    if (!expression)
        return null;

    const parts = expression.split('.').filter(Boolean);
    if (parts.length === 1 && (parts[0] === 'Control' || parts[0] === 'this'))
        return { root: parts[0], member: null };

    if (parts.length === 2 && (parts[0] === 'Control' || parts[0] === 'this'))
        return { root: parts[0], member: parts[1] };

    return null;
}

/**
 * @param {unknown} node
 * @returns {boolean}
 */
function isDesignControlNode(node) {
    return !!node
        && typeof node === 'object'
        && 'ControlType' in node
        && node.ControlData
        && typeof node.ControlData === 'object';
}

/**
 * Recursively collect embedded UserControl references from a design model.
 * @param {unknown} root
 * @returns {{ name: string, kind: 'userControlRef', controlURL: string }[]}
 */
export function listEmbeddedUserControlsInDesign(root) {
    return listNamedControlsInDesign(root).filter((item) => item.kind === 'userControlRef');
}

/**
 * Recursively collect named controls from a UserControl design model.
 * @param {unknown} root
 * @returns {{ name: string, kind: 'control' | 'userControlRef', controlType?: string, controlURL?: string }[]}
 */
export function listNamedControlsInDesign(root) {
    const byName = new Map();

    function recordControl(node) {
        const embedded = isEmbeddedUserControlNode(node);
        const name = resolveControlScopeName(node);
        if (!isUsableControlName(name) || byName.has(name))
            return;

        if (embedded) {
            byName.set(name, {
                name,
                kind: 'userControlRef',
                controlURL: String(node.ControlURL || '').trim(),
            });
            return;
        }

        if (!isDesignControlNode(node))
            return;

        byName.set(name, {
            name,
            kind: 'control',
            controlType: String(node.ControlType || 'Control'),
        });
    }

    function walk(node) {
        if (!node || typeof node !== 'object')
            return;

        if (isEmbeddedUserControlNode(node) || isDesignControlNode(node))
            recordControl(node);

        if (Array.isArray(node)) {
            for (const item of node)
                walk(item);
            return;
        }

        for (const key of Object.keys(node)) {
            if (key.startsWith('$'))
                continue;
            walk(node[key]);
        }
    }

    walk(root);
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * List UserControl parameter names from ControlDataSchema.properties.
 * @param {unknown} controlDataSchema
 * @returns {{ name: string, kind: 'param', paramType?: string }[]}
 */
export function listControlDataSchemaParams(controlDataSchema) {
    const properties = controlDataSchema?.properties;
    if (!properties || typeof properties !== 'object')
        return [];

    const members = [];

    for (const name of Object.keys(properties)) {
        if (!isValidJsIdentifier(name))
            continue;

        const property = properties[name];
        const paramType = typeof property === 'object' && property
            ? String(property.type || property.title || 'parameter')
            : 'parameter';

        members.push({
            name,
            kind: 'param',
            paramType,
        });
    }

    return members.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @param {unknown} controlDataSchema
 * @returns {{ name: string, kind: 'param', paramType?: string }[]}
 */
export function buildParamsDotCompletions(controlDataSchema) {
    return listControlDataSchemaParams(controlDataSchema);
}

/**
 * List ActionSet entries as Control bag function members.
 * @param {unknown} actionSet
 * @returns {{ name: string, kind: 'function', runtimeSource: 'actionSet' }[]}
 */
export function listActionSetMembers(actionSet) {
    if (!Array.isArray(actionSet))
        return [];

    const byName = new Map();

    for (const entry of actionSet) {
        const name = String(entry?.Name || '').trim();
        if (!name || !isValidJsIdentifier(name) || byName.has(name))
            continue;

        byName.set(name, {
            name,
            kind: 'function',
            runtimeSource: 'actionSet',
        });
    }

    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * List Commands entries exposed on the Control bag (InternalOnly / InternalAndExternal).
 * @param {unknown} commands
 * @returns {{ name: string, kind: 'function', runtimeSource: 'commands' }[]}
 */
export function listCommandMembers(commands) {
    if (!Array.isArray(commands))
        return [];

    const byName = new Map();

    for (const entry of commands) {
        const name = String(entry?.Name || '').trim();
        const visibility = String(entry?.Visibility || '').trim();
        if (!name || !isValidJsIdentifier(name) || byName.has(name))
            continue;
        if (!INTERNAL_COMMAND_VISIBILITY.has(visibility))
            continue;

        byName.set(name, {
            name,
            kind: 'function',
            runtimeSource: 'commands',
        });
    }

    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @param {unknown} designRoot
 * @param {string} scopeScriptText
 * @param {unknown} [commands]
 * @returns {{ name: string, kind: 'control' | 'function' | 'variable' | 'userControlRef', controlType?: string, runtimeSource?: string }[]}
 */
export function buildControlDotCompletions(designRoot, scopeScriptText, commands) {
    const controls = listNamedControlsInDesign(designRoot);
    const controlNames = new Set(controls.map((item) => item.name));
    const commandMembers = listCommandMembers(commands)
        .filter((member) => !controlNames.has(member.name));
    const commandNames = new Set(commandMembers.map((member) => member.name));
    const scriptMembers = listScopeScriptMembers(scopeScriptText)
        .filter((member) => !controlNames.has(member.name) && !commandNames.has(member.name));

    return [...controls, ...commandMembers, ...scriptMembers];
}

/**
 * @param {{ name: string }[]} members
 * @param {string[]|null|undefined} scopePublic
 * @returns {{ name: string, kind?: string, controlType?: string }[]}
 */
export function filterMembersByPublic(members, scopePublic) {
    if (!scopePublic || !scopePublic.length)
        return [];

    const allowed = new Set(scopePublic);
    return (members || []).filter((member) => allowed.has(member.name));
}

/**
 * @param {import('monaco-editor')} monaco
 * @param {{ name: string, kind?: string, controlType?: string, libraryPath?: string }[]} members
 * @param {import('monaco-editor').IRange} range
 * @returns {import('monaco-editor').languages.CompletionItem[]}
 */
export function buildCompletionSuggestions(monaco, members, range) {
    return (members || []).map((member, index) => {
        let kind = monaco.languages.CompletionItemKind.Variable;
        let detail = '';

        if (member.kind === 'control') {
            kind = monaco.languages.CompletionItemKind.Class;
            detail = `${member.controlType || 'Control'} control`;
        }
        else if (member.kind === 'userControlRef') {
            kind = monaco.languages.CompletionItemKind.Module;
            detail = member.controlURL
                ? `UserControl reference (${member.controlURL})`
                : 'UserControl reference';
        }
        else if (member.kind === 'param') {
            kind = monaco.languages.CompletionItemKind.Field;
            detail = `Params.${member.paramType || 'parameter'}`;
        }
        else if (member.kind === 'function') {
            kind = monaco.languages.CompletionItemKind.Function;
            if (member.runtimeSource === 'controlModel' && member.parameters)
                detail = `${member.name}(${member.parameters.join(', ')})`;
            else if (member.runtimeSource === 'api')
                detail = 'api method';
            else if (member.runtimeSource === 'util')
                detail = 'util helper';
            else if (member.runtimeSource === 'commands')
                detail = 'Commands command';
            else if (member.runtimeSource === 'actionSet')
                detail = 'ActionSet action';
            else if (member.embeddedControlName)
                detail = `public member of ${member.embeddedControlName}`;
            else if (member.libraryPath)
                detail = `public member of ${member.libraryPath}`;
            else
                detail = 'ScopeScript member';
        }
        else {
            kind = monaco.languages.CompletionItemKind.Variable;
            if (member.runtimeSource === 'controlModel')
                detail = 'controlModel property';
            else if (member.embeddedControlName)
                detail = `public member of ${member.embeddedControlName}`;
            else
                detail = member.libraryPath
                    ? `public member of ${member.libraryPath}`
                    : 'ScopeScript member';
        }

        const suggestion = {
            label: member.name,
            kind,
            insertText: member.name,
            detail,
            sortText: String(index).padStart(4, '0'),
            range,
        };

        if (member.description) {
            suggestion.documentation = {
                value: member.description,
                isTrusted: true,
            };
        }

        return suggestion;
    });
}

function isKnownLibraryAlias(expression, deps) {
    const map = deps.getLibraryMembersByAlias?.() || {};
    if (Object.prototype.hasOwnProperty.call(map, expression))
        return true;

    const includes = deps.getScopeIncludes?.() || [];
    return includes.some((inc) => String(inc?.As || '').trim() === expression);
}

function isScopeObjectExpression(expression) {
    return expression === 'Control' || expression === 'this';
}

/**
 * Resolve completion members for the expression before a dot.
 * @param {string} expression
 * @param {{
 *   getDesignRoot: () => unknown,
 *   getScopeScriptText: () => string,
 *   getCommands?: () => unknown,
 *   getLibraryMembersByAlias: () => Record<string, { name: string, kind?: string, libraryPath?: string }[]>,
 *   getEmbeddedUserControlMembersByName?: () => Record<string, { name: string, kind?: string, embeddedControlName?: string }[]>,
 *   getScopeIncludes?: () => { As?: string }[],
 * }} deps
 * @returns {{ name: string, kind?: string, controlType?: string, libraryPath?: string }[]}
 */
export function resolveDotCompletionMembers(expression, deps) {
    if (!expression)
        return [];

    const scopeAccess = parseScopeAccessExpression(expression);
    if (scopeAccess?.member) {
        const embeddedMap = deps.getEmbeddedUserControlMembersByName?.() || {};
        if (Object.prototype.hasOwnProperty.call(embeddedMap, scopeAccess.member))
            return embeddedMap[scopeAccess.member] || [];

        return [];
    }

    if (scopeAccess || isScopeObjectExpression(expression))
        return buildControlDotCompletions(
            deps.getDesignRoot(),
            deps.getScopeScriptText(),
            deps.getCommands?.(),
        );

    if (expression === 'controlModel')
        return buildControlModelDotCompletions();

    if (expression === 'Params')
        return buildParamsDotCompletions(deps.getControlDataSchema?.());

    if (expression === 'api')
        return buildApiDotCompletions();

    if (expression === 'util')
        return deps.getUtilDotCompletions?.() || [];

    if (isKnownLibraryAlias(expression, deps)) {
        const map = deps.getLibraryMembersByAlias?.() || {};
        return map[expression] || [];
    }

    return [];
}

/**
 * @param {{
 *   getScopeScriptText: () => string,
 *   getScopeIncludes?: () => { As?: string, Path?: string }[],
 * }} deps
 * @returns {{ name: string, kind?: string, libraryPath?: string }[]}
 */
export function buildTopLevelCompletions(deps) {
    const byName = new Map();

    byName.set('Control', { name: 'Control', kind: 'variable' });
    byName.set('Params', { name: 'Params', kind: 'variable' });
    byName.set('api', { name: 'api', kind: 'variable' });
    byName.set('controlModel', { name: 'controlModel', kind: 'variable', runtimeSource: 'controlModel' });
    byName.set('util', { name: 'util', kind: 'variable' });
    byName.set('this', { name: 'this', kind: 'variable' });

    for (const item of deps.getScopeIncludes?.() || []) {
        const alias = String(item?.As || '').trim();
        if (!alias || !isValidJsIdentifier(alias) || byName.has(alias))
            continue;

        byName.set(alias, {
            name: alias,
            kind: 'variable',
            libraryPath: item?.Path,
        });
    }

    for (const member of listScopeScriptMembers(deps.getScopeScriptText())) {
        if (!byName.has(member.name))
            byName.set(member.name, member);
    }

    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Disable word-based suggestions so identifiers elsewhere in the buffer are not
 * mixed into scoped dot completion lists.
 * @param {import('monaco-editor').editor.IStandaloneCodeEditor} editor
 */
export function configureScopeScriptEditorSuggestOptions(editor) {
    editor.updateOptions({
        wordBasedSuggestions: 'off',
    });
}

/**
 * @param {{
 *   monaco: import('monaco-editor'),
 *   model: import('monaco-editor').editor.ITextModel,
 *   getDesignRoot: () => unknown,
 *   getScopeScriptText: () => string,
 *   getCommands?: () => unknown,
 *   getControlDataSchema: () => unknown,
 *   getUtilDotCompletions?: () => { name: string, kind?: string, runtimeSource?: string }[],
 *   getEmbeddedUserControlMembersByName?: () => Record<string, { name: string, kind?: string, embeddedControlName?: string }[]>,
 *   getLibraryMembersByAlias: () => Record<string, { name: string, kind?: string, libraryPath?: string }[]>,
 *   getScopeIncludes?: () => { As?: string }[],
 * }} options
 * @returns {{ dispose: () => void }}
 */
export function createScopeScriptCompletionProvider(options) {
    const {
        monaco,
        model,
        getDesignRoot,
        getScopeScriptText,
        getCommands,
        getControlDataSchema,
        getUtilDotCompletions,
        getEmbeddedUserControlMembersByName,
        getLibraryMembersByAlias,
        getScopeIncludes,
    } = options;

    const deps = {
        getDesignRoot,
        getScopeScriptText,
        getCommands,
        getControlDataSchema,
        getUtilDotCompletions,
        getEmbeddedUserControlMembersByName,
        getLibraryMembersByAlias,
        getScopeIncludes,
    };

    const provider = monaco.languages.registerCompletionItemProvider(CONTROL_SCOPE_SCRIPT_LANGUAGE_ID, {
        triggerCharacters: ['.'],
        provideCompletionItems(editorModel, position) {
            if (editorModel !== model)
                return { suggestions: [] };

            const line = editorModel.getLineContent(position.lineNumber);
            const textBeforeCursor = line.substring(0, position.column - 1);

            if (textBeforeCursor.endsWith('.')) {
                const expression = extractMemberAccessExpression(textBeforeCursor);
                const members = resolveDotCompletionMembers(expression, deps);

                const word = editorModel.getWordUntilPosition(position);
                const range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: position.column,
                };

                return {
                    suggestions: buildCompletionSuggestions(monaco, members, range),
                };
            }

            const word = editorModel.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };

            return {
                suggestions: buildCompletionSuggestions(monaco, buildTopLevelCompletions(deps), range),
            };
        },
    });

    return provider;
}
